import { LocalAtm, RemoveCircleOutline } from '@material-ui/icons';
import { Button } from '@vadiun/react-components';
import { Discount } from 'app/discounts/models/discountsModel';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import moment from 'moment';
import React, { FC } from 'react';
import PercentageField from 'shared/components/form/PercentageField';
import * as Yup from 'yup';
interface CreateEditDiscountFormProps {
  onSubmit: (values: any) => void;
  initialValues?: any;
  isEditing?: boolean;
  onCancel: () => void;
  existingDiscounts: Discount[];
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('El nombre es requerido')
    .min(3, 'El nombre no puede ser menor a 3 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),
  percentage: Yup.number()
    .required('El porcentaje es requerido')
    .min(0, 'El porcentaje no puede ser menor a 0')
    .max(100, 'El porcentaje no puede ser mayor a 100'),
  from_date: Yup.date()
    .required('La fecha de inicio es requerida')
    .typeError('Fecha inválida'),

  end_date: Yup.date()
    .required('La fecha de fin es requerida')
    .typeError('Fecha inválida')
    .min(
      Yup.ref('from_date'),
      'La fecha de fin no puede ser anterior a la de inicio'
    )
    .test(
      'not-same-date',
      'La fecha de fin no puede ser igual a la de inicio',
      function (value) {
        return value?.getTime() !== this.parent.from_date?.getTime();
      }
    )
});

const initialFormValues: any = {
  name: '',
  percentage: null,
  from_date: moment(),
  end_date: moment()
};
const CreateEditDiscountForm: FC<CreateEditDiscountFormProps> = ({
  onSubmit,
  initialValues = initialFormValues,
  isEditing,
  onCancel,
  existingDiscounts
}) => {
  const modifiedInitialValues = isEditing
    ? initialValues
    : { ...initialFormValues };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  const normalizeDate = (date: string | Date): Date => {
    return date instanceof Date
      ? date
      : (() => {
          const [y, m, d] = date.split('T')[0].split('-').map(Number);
          return new Date(y, m - 1, d);
        })();
  };
  const isDateDisabled = (date: Date, existingDiscounts: Discount[]) => {
    if (!existingDiscounts) return false;

    return existingDiscounts.some((discount) => {
      const startDate = normalizeDate(discount.from_date);
      const endDate = normalizeDate(discount.end_date);

      return date >= startDate && date <= endDate;
    });
  };

  return (
    <Formik
      initialValues={modifiedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        isValid,
        dirty,
        touched,
        errors,
        submitCount,
        values,
        setFieldValue
      }) => (
        <Form
          className="w-full flex bg-white rounded-xl shadow-lg border border-gray-100 gap-4 p-6
    transition-[width] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] relative overflow-hidden "
        >
          <div className="animate-header-slide flex w-full flex-col gap-4  border-r-4 border-blue-300 mr-2 ">
            <div className="flex flex-col w-full items-center gap-4 ">
              <div className="flex w-auto relative mb-2">
                <LocalAtm
                  style={{
                    fontSize: 'clamp(80px, 15vw, 120px)',
                    color: '#00CF8A',
                    marginBottom: '0.5rem',
                    borderRadius: '50%',
                    padding: '5px',
                    boxShadow: '0 6px 8px rgba(0,207,138,0.5)'
                  }}
                />
                <RemoveCircleOutline
                  style={{
                    fontSize: 'clamp(30px, 5vw, 50px)',
                    color: '#FF2700', // Verde
                    position: 'absolute',
                    right: -10,
                    bottom: -4,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: '4px',
                    boxShadow: '0 4px 4px rgba(255,39,0,0.5)'
                  }}
                />
              </div>
              <h2
                className="text-3xl font-bold text-center px-2 rounded-md"
                style={{
                  color: '#01784F',
                  textShadow: '2px 2px 3px #61FFD0'
                }}
              >
                {isEditing ? 'Editar Descuento' : 'Nuevo Descuento'}
              </h2>
            </div>
          </div>

          <div className="animate-form-expand hidden:delay-700 flex flex-col w-full  justify-between">
            <div className="flex justify-between ">
              <div className="container space-y-6 ">
                <div className="flex justify-between gap-4">
                  <Field
                    name="name"
                    label="Nombre"
                    variant="outlined"
                    component={TextField}
                    fullWidth
                  />
                  <PercentageField
                    name="percentage"
                    label="Porcentaje"
                    className="col-span-3"
                  />
                </div>
                <div className="flex justify-between gap-4">
                  <Field
                    component={KeyboardDateTimePicker}
                    label="Fecha de inicio"
                    name="from_date"
                    ampm={false}
                    setError={() => console.log('')}
                    inputVariant="outlined"
                    margin="normal"
                    className="flex-1"
                    format="DD/MM/YYYY HH:mm"
                    shouldDisableDate={(date: Date) =>
                      isDateDisabled(date, existingDiscounts)
                    }
                  />
                  <Field
                    component={KeyboardDateTimePicker}
                    label="Fecha de finalización"
                    name="end_date"
                    ampm={false}
                    setError={() => console.log('')}
                    inputVariant="outlined"
                    margin="normal"
                    className="flex-1"
                    format="DD/MM/YYYY HH:mm"
                    shouldDisableDate={(date: Date) =>
                      isDateDisabled(date, existingDiscounts)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-between  pt-4">
              <Button
                type="button"
                variant="light"
                color="gray"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty}
              >
                {isEditing ? 'Actualizar Descuento' : 'Crear Descuento'}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CreateEditDiscountForm;
